import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  Warning,
  Build,
  UploadFile,
  SmartToy,
  GroupsOutlined,
  Security,
  School
} from '@mui/icons-material';

const DISCLAIMER_SHOWN_KEY = 'aidivecoach_disclaimer_shown';

/** Dispatch on window to reopen the full disclaimer (e.g. from the footer link). */
export const SHOW_DISCLAIMER_EVENT = 'aidivecoach:show-disclaimer';

interface DisclaimerDialogProps {
  /** Called once, synchronously on mount, with whether the dialog will be shown. */
  onPendingChange?: (pending: boolean) => void;
}

const DisclaimerDialog: React.FC<DisclaimerDialogProps> = ({ onPendingChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Check if disclaimer has been shown before
    const disclaimerShown = localStorage.getItem(DISCLAIMER_SHOWN_KEY);
    if (!disclaimerShown) {
      setOpen(true);
      onPendingChange?.(true);
    } else {
      onPendingChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(SHOW_DISCLAIMER_EVENT, reopen);
    return () => window.removeEventListener(SHOW_DISCLAIMER_EVENT, reopen);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(DISCLAIMER_SHOWN_KEY, 'true');
    setOpen(false);
    onPendingChange?.(false);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: isDark
            ? 'linear-gradient(135deg, #1e293b 0%, #1f2937 100%)'
            : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#d73502',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Warning fontSize="large" />
        <Box>
          <Typography variant="h5" component="div">
            New Achievement Scuba - Important Disclaimer
          </Typography>
          <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
            Please read carefully before using this application
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Safety Disclaimer */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="error" />
            Safety & Medical Disclaimer
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>This application does NOT replace professional medical advice, dive training, or your own judgment.</strong> 
            All dive data, calculations, and information provided are for educational and recreational purposes only.
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            • Always consult with certified dive instructors and medical professionals<br/>
            • Use your own common sense and training when making diving decisions<br/>
            • Verify all calculations and data independently<br/>
            • Never rely solely on any app for dive planning or safety decisions
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Application Features & Limitations */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Build color="primary" />
          What This App Does
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon>
              <GroupsOutlined color="secondary" />
            </ListItemIcon>
            <ListItemText
              primary="A Community of Divers"
              secondary="Share dives and photos in your feed, add dive buddies, follow other divers, join groups and clubs, message each other, and plan dives and trips together."
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <UploadFile color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Your Dive Log, Built In"
              secondary="Upload dive data extracts from your computer or logging software (FIT and UDDF files), sync automatically by connecting Suunto, or enter dives by hand. Mix and match — no third-party account required. Direct integration with other dive computer manufacturers is coming soon."
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <SmartToy color="action" />
            </ListItemIcon>
            <ListItemText
              primary="Nereus — Your AI Dive Assistant (Still Learning)"
              secondary="Nereus can answer diving questions and talk through your dive history. He is still being trained, so his answers can be incomplete or wrong — treat them as a starting point for discussion, never as instruction or a dive plan."
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <School color="info" />
            </ListItemIcon>
            <ListItemText
              primary="Planning, Training & Analysis Tools"
              secondary="Dive site directory, equipment tracking, flashcards, buoyancy and decompression calculators, and dive statistics — all provided for learning and tracking purposes only."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Data Privacy Notice */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Privacy:</strong> Your dive data stays with you. Manually entered and uploaded dives are stored in your account only. If you connect Suunto, data is accessed directly from their API with your permission. We only process data necessary for the application features.
          </Typography>
        </Alert>

        {/* Final Warning */}
        <Alert
          severity="error"
          variant="filled"
          icon={<Warning fontSize="large" />}
          sx={{
            alignItems: 'center',
            border: '2px solid',
            borderColor: 'error.dark',
            boxShadow: 3,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.5 }}>
            By using this application, you acknowledge that diving carries inherent risks
            and that you are solely responsible for your safety decisions.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
        <Button
          onClick={handleAccept}
          variant="contained"
          size="large"
          sx={{
            minWidth: 200,
            py: 1.5,
            background: 'linear-gradient(45deg, #00ACC1 30%, #26A69A 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #00838F 30%, #00695C 90%)',
            }
          }}
        >
          I Understand & Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisclaimerDialog;